import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LiveKitRoom, VideoConference, RoomAudioRenderer } from '@livekit/components-react';
import '@livekit/components-styles';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { GiftPanel } from '../components/GiftPanel';
import { ChatPanel } from '../components/ChatPanel';
import StreamAccessGate from '../components/StreamAccessGate';
import { API } from '../config/api';
import { ViewerJoinButton } from '../components/LiveGuestSystem';
import LiveReactionPanel from '../components/LiveReactionPanel';

import {
  Radio, Users, AlertCircle, Loader2, ArrowLeft, Gift,
  MessageCircle, RefreshCw, WifiOff, MonitorPlay, Eye
} from 'lucide-react';


function ViewerConnectionOverlay({ state }) {
  if (state === 'connected') return null;

  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-40" data-testid="viewer-connection-overlay">
      {state === 'connecting' && (
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-white font-medium">Joining stream...</p>
          <p className="text-white/50 text-xs mt-1">Setting up video connection</p>
        </div>
      )}
      {state === 'reconnecting' && (
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-amber-400 mx-auto mb-3" />
          <p className="text-white font-medium">Reconnecting...</p>
          <p className="text-white/50 text-xs mt-1">This may take a moment</p>
        </div>
      )}
      {state === 'disconnected' && (
        <div className="text-center">
          <WifiOff className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-white font-medium">Connection Lost</p>
          <p className="text-white/50 text-xs mt-1">Attempting to reconnect...</p>
        </div>
      )}
      {state === 'video_unavailable' && (
        <div className="text-center">
          <MonitorPlay className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-white font-medium">Video Unavailable</p>
          <p className="text-white/50 text-xs mt-1">Host video may be starting. Audio is still active.</p>
        </div>
      )}
    </div>
  );
}


export default function WatchStreamPage() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stream, setStream] = useState(null);
  const [token, setToken] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [connectionState, setConnectionState] = useState('connecting');
  const [error, setError] = useState(null);
  const [showGiftPanel, setShowGiftPanel] = useState(false);
  const [recentGifts, setRecentGifts] = useState([]);
  const [topFans, setTopFans] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessCheck, setAccessCheck] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);

  // Poll viewer count every 10s
  useEffect(() => {
    if (!streamId || !stream) return;
    const pollViewers = () => {
      fetch(`${API}/live/streams/${streamId}`, { headers: getAuthHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d) {
            setViewerCount(d.viewer_count || 0);
            if (d.status === 'ended') {
              toast.info('Stream has ended');
              navigate('/live');
            }
          }
        })
        .catch(() => {});
    };
    const interval = setInterval(pollViewers, 10000);
    return () => clearInterval(interval);
  }, [streamId, stream]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (streamId) checkAccessAndInitialize();
    return () => { if (streamId) recordViewerLeft(); };
  }, [streamId, isAuthenticated]);

  const checkAccessAndInitialize = async () => {
    try {
      const accessRes = await fetch(`${API}/live/streams/${streamId}/access-check`, { headers: getAuthHeaders() });
      if (accessRes.ok) {
        const accessData = await accessRes.json();
        setAccessCheck(accessData);
        if (!accessData.has_access) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
      }
      await initializeViewer();
    } catch {
      await initializeViewer();
    }
  };

  const initializeViewer = async () => {
    try {
      const streamRes = await fetch(`${API}/live/streams/${streamId}`, { headers: getAuthHeaders() });
      if (!streamRes.ok) {
        const errData = await streamRes.json().catch(() => ({}));
        throw new Error(errData?.detail || `Stream not found (${streamRes.status})`);
      }
      const streamData = await streamRes.json();
      if (streamData.status === 'ended') throw new Error('This stream has ended');
      setStream(streamData);
      setViewerCount(streamData.viewer_count || 0);

      // Fetch top fans
      if (streamData.host_artist_id) {
        try {
          const topFansRes = await fetch(`${API}/artists/${streamData.host_artist_id}/top-fans?limit=10`);
          if (topFansRes.ok) setTopFans((await topFansRes.json()).top_fans || []);
        } catch {}
      }

      // Get viewer token
      const tokenRes = await fetch(`${API}/live/token/${streamId}`, { headers: getAuthHeaders() });
      if (!tokenRes.ok) {
        const errData = await tokenRes.json().catch(() => ({}));
        throw new Error(errData?.detail || `Failed to join stream (${tokenRes.status})`);
      }
      const tokenData = await tokenRes.json();
      if (!tokenData?.token || !tokenData?.url) throw new Error('Missing stream connection data');
      setToken(tokenData.token);
      setServerUrl(tokenData.url);
    } catch (err) {
      const msg = (err instanceof Error) ? err.message : 'Failed to join stream';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const recordViewerLeft = async () => {
    try {
      await fetch(`${API}/live/streams/${streamId}/viewer-left`, { method: 'POST', headers: getAuthHeaders() });
    } catch {}
  };

  const handleGiftSent = (giftData) => {
    setRecentGifts(prev => [giftData, ...prev].slice(0, 5));
  };

  const handleLeave = () => {
    recordViewerLeft();
    navigate('/live');
  };

  // Access denied
  if (accessDenied && accessCheck) {
    return (
      <div className="min-h-screen bg-background">
        <StreamAccessGate streamId={streamId} accessCheck={accessCheck}
          onClose={() => navigate('/live')} isAuthenticated={isAuthenticated} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Joining stream...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full glass-card border-red-500/30 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Unable to Join Stream</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.reload()} className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
            <Button onClick={() => navigate('/live')} className="flex-1">Browse Streams</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!token || !serverUrl) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full glass-card border-amber-500/30 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Unable to Connect</h1>
          <p className="text-muted-foreground mb-4">Live streaming connection could not be established.</p>
          <Button onClick={() => navigate('/live')}>Browse Streams</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen bg-black flex flex-col" data-testid="watch-stream-page">
      {/* Header */}
      <div className="bg-background/80 backdrop-blur-md border-b border-border p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleLeave} data-testid="leave-stream-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Badge className="bg-red-500 text-white animate-pulse" data-testid="live-badge">
            <Radio className="w-3 h-3 mr-1" /> LIVE
          </Badge>
          <div>
            <h1 className="font-heading font-bold truncate max-w-[300px]">{stream?.title}</h1>
            <p className="text-sm text-muted-foreground">{stream?.host_name}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="viewer-count">
            <Eye className="w-4 h-4" />
            <span>{viewerCount} watching</span>
          </div>

          <ViewerJoinButton streamId={streamId} />

          <Sheet open={showGiftPanel} onOpenChange={setShowGiftPanel}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="border-secondary/50 text-secondary" data-testid="open-gift-panel-btn">
                <Gift className="w-4 h-4 mr-2" /> Gift
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-background border-border">
              <SheetHeader><SheetTitle>Send a Gift</SheetTitle></SheetHeader>
              <GiftPanel streamId={streamId} getAuthHeaders={getAuthHeaders} onGiftSent={handleGiftSent} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* LiveKit Room — Viewer Mode */}
      <div className="flex-1 flex">
        <div className="flex-1 relative">
          <ViewerConnectionOverlay state={connectionState} />
          <LiveKitRoom
            token={token}
            serverUrl={serverUrl}
            connect={true}
            video={false}
            audio={false}
            onConnected={() => setConnectionState('connected')}
            onDisconnected={() => setConnectionState('disconnected')}
            onReconnecting={() => setConnectionState('reconnecting')}
            onReconnected={() => setConnectionState('connected')}
            style={{ height: '100%' }}
          >
            <VideoConference />
            <RoomAudioRenderer />
          </LiveKitRoom>
        </div>

        {/* Chat Sidebar */}
        <div className="w-[320px] border-l border-border bg-background/50 hidden lg:flex flex-col">
          <div className="p-3 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <MessageCircle className="w-4 h-4" /> Live Chat
            </h3>
          </div>
          <ChatPanel
            streamId={streamId}
            getAuthHeaders={getAuthHeaders}
            hostUserId={stream?.host_user_id}
            topFans={topFans}
            isHost={false}
            currentUserId={isAuthenticated ? user?.user_id : null}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="bg-background/90 border-t border-border p-3">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-muted-foreground">{stream?.description}</p>
        </div>
      </div>

      {stream?.status === 'live' && (
        <LiveReactionPanel streamId={streamId} isHost={false} />
      )}
    </div>
  );
}
